import * as Yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { FormProvider, RHFSelect, RHFTextField } from 'src/components/hook-form';
import {
  Button,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  SelectChangeEvent,
  Stack,
} from '@mui/material';
import { useEffect, useState, useMemo, useCallback } from 'react';
import { getPredefinedNote } from 'src/careflow/helpers/common';
import useLocales from 'src/careflow/ph-core/internationalization/hook/useLocales';
import { isEmpty } from 'lodash';
import { useValueSet } from 'src/careflow/ph-core/fhir-client/fhir-react';
import { ValueSetWrapper } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/ValueSet';
import { convertValueToValueSet } from 'src/careflow/common/common-utils';
import useAuth from 'src/careflow/ph-core/authentication/hook/useAuth';
import { getFhirIdFromEntity } from 'src/utils/fhir';
import { useSnackbar } from 'notistack';
import { WrappedPatient } from 'src/careflow/ph-core/fhir-client/fhir-base/wrappers/Patient';
import DialogCloseButton from 'src/components/DialogCloseButton';

type FormValues = {
  predefinedNote: string;
  text: string;
  topic: string;
  medium: string;
};

type Props = {
  openDialog: boolean;
  patient: WrappedPatient | null;
  patientEngagement?: any;
  onCreate: (data: any) => Promise<void>;
  onClose: () => void;
};

export default function AddNote({
  openDialog,
  patient,
  patientEngagement,
  onCreate,
  onClose,
}: Props) {
  const { i18n } = useLocales();
  const authUser = useAuth().getCurrentUser();
  const { enqueueSnackbar } = useSnackbar();

  const [predefinedNotes, setPredefinedNotes] = useState<any>(null);
  const [disabledButton, setDisabledButton] = useState<boolean>(true);

  const [topicList] = useValueSet('communication-topic', { map: ValueSetWrapper });
  const [communicationMediumList] = useValueSet('v3-ParticipationMode', { map: ValueSetWrapper });
  const [communicationCategory] = useValueSet('ph-communication-categories', {
    map: ValueSetWrapper,
  });

  const categoryCommunicationCoding = convertValueToValueSet(
    'notes_patient_engagement',
    communicationCategory
  );

  const schema = Yup.object().shape({
    text: Yup.string().required('Text is required'),
    topic: Yup.string().required('Topic is required'),
  });

  const defaultValues = {
    text: '',
    predefinedNote: '',
    topic: '',
    medium: '',
  };

  const methods = useForm<FormValues>({ resolver: yupResolver(schema), defaultValues });

  const {
    reset,
    setValue,
    watch,
    handleSubmit,
    formState: { isSubmitting },
  } = methods;

  const { text } = watch();

  useEffect(() => {
    let regex = /[{}]/;
    setDisabledButton(true);
    if (!regex.test(text) && !isEmpty(text)) setDisabledButton(false);
  }, [text]);

  const handlerClose = () => {
    onClose();
    reset(defaultValues);
  };

  const mapFormDataToCommunication = (data: FormValues): any => {
    const mappedData: any = { resourceType: 'Communication' };

    mappedData.status = 'completed';
    mappedData.sent = new Date();
    mappedData.category = [
      {
        coding: [categoryCommunicationCoding],
        text: categoryCommunicationCoding?.display,
      },
    ];
    mappedData.subject = {
      reference: `Patient/${patient?.id}`,
      display: patient?.getFullName(),
    };
    mappedData.sender = {
      ...(!isEmpty(authUser.user_fhir_uri.trim()) && {
        reference: `Practitioner/${getFhirIdFromEntity(authUser.user_fhir_uri)}`,
      }),
      display: authUser.name,
    };

    if (data?.text) {
      mappedData.payload = [
        {
          contentString: data.text,
        },
        {
          contentReference: {
            display: data.text,
          },
        },
      ];
      mappedData.note = [
        {
          text: data.text.replace(/<[^>]+>/g, ''),
          authorReference: {
            reference: `Practitioner/${getFhirIdFromEntity(authUser.user_fhir_uri)}`,
            type: 'Practitioner',
          },
          time: new Date().toISOString(),
        },
      ];
      mappedData.text = {
        div: data.text,
        status: 'empty',
      };
    }

    if (data?.topic) {
      const topicValueSet = convertValueToValueSet(data.topic, topicList);
      mappedData.topic = {
        coding: [
          {
            ...topicValueSet,
          },
        ],
        text: topicValueSet?.display,
      };
    }

    if (data?.medium) {
      const mediumValueSet = convertValueToValueSet(data.medium, communicationMediumList);
      if (mediumValueSet) {
        mappedData.medium = [
          {
            coding: [
              {
                ...mediumValueSet,
              },
            ],
            ...(mediumValueSet?.display && { text: mediumValueSet.display }),
          },
        ];
      }
    }

    return mappedData;
  };

  const onSubmit = async (values: FormValues) => {
    try {
      const noteData = mapFormDataToCommunication(values);
      await onCreate(noteData);
      enqueueSnackbar('Note created successfully!');
      handlerClose();
    } catch (error) {
      console.error('Error creating note:', error);
      enqueueSnackbar('Failed to create note', {
        variant: 'error',
        autoHideDuration: 6000,
      });
    }
  };

  const resource = useMemo(() => {
    if (!patientEngagement) return null;

    return {
      referralRecord: {
        patientName: patientEngagement.patientName,
        patientManagingOrgDisplay: patientEngagement.patientManagingOrgDisplay,
        patientDOBFormatted: patientEngagement.patientDOBFormatted,
        workflowStageName: patientEngagement.workflowStageName,
      },
    };
  }, [
    patientEngagement?.patientName,
    patientEngagement?.patientManagingOrgDisplay,
    patientEngagement?.patientDOBFormatted,
    patientEngagement?.workflowStageName,
  ]);

  const getPredefinedNoteCustom = useCallback(async () => {
    if (!patient) return;

    const data = await getPredefinedNote(
      resource || patient,
      'notes',
      'engagement-predefined-notes',
      false
    );
    setPredefinedNotes(data);
  }, [patient, resource]);

  useEffect(() => {
    getPredefinedNoteCustom();
  }, [getPredefinedNoteCustom]);

  const handleChangePredefinedNote = (event: SelectChangeEvent<unknown>) => {
    setDisabledButton(true);
    const setDescription =
      predefinedNotes?.find((e) => e.code === event.target.value)?.display ?? '';
    setValue('predefinedNote', String(event.target.value));
    setValue('text', setDescription);
  };

  return (
    <Dialog
      open={openDialog}
      onClose={handlerClose}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullWidth
      maxWidth="md"
    >
      <DialogCloseButton
        onClose={handlerClose}
        hasUnsavedChanges={methods.formState.isDirty}
        disabled={isSubmitting}
      />
      <DialogTitle id="alert-dialog-title"> Add Note</DialogTitle>
      <DialogContent>
        <CardContent>
          <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Grid container direction="column" spacing={3}>
              <Grid item md={12}>
                <Stack spacing={2} sx={{ p: 2 }}>
                  <RHFSelect
                    name="topic"
                    label={`${i18n('patients.details.notes.topic', 'crs')}*`}
                    fullWidth
                  >
                    <MenuItem disabled />
                    {topicList?.asList().map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        {option?.display}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Stack>
              </Grid>

              <Grid item md={12}>
                <Stack spacing={2} sx={{ p: 2 }}>
                  <RHFSelect
                    name="medium"
                    label={i18n('patients.details.notes.medium', 'crs')}
                    fullWidth
                  >
                    <MenuItem disabled />
                    {communicationMediumList?.asSortedListAll().map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        {option?.display || option?.code}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Stack>
              </Grid>

              <Grid item md={12}>
                {predefinedNotes?.length > 0 && (
                  <Stack spacing={2} sx={{ p: 2 }}>
                    <RHFSelect
                      name="predefinedNote"
                      label={i18n('patients.details.notes.predefinedNote', 'crs')}
                      onChange={(event) => handleChangePredefinedNote(event)}
                      fullWidth
                    >
                      <MenuItem disabled />
                      {predefinedNotes?.map((option) => (
                        <MenuItem key={option.code} value={option.code}>
                          {option?.display}
                        </MenuItem>
                      ))}
                    </RHFSelect>
                  </Stack>
                )}
              </Grid>

              <Grid item md={12}>
                <Stack spacing={2} sx={{ p: 2 }}>
                  <RHFTextField
                    name="text"
                    label={`${i18n('patients.details.notes.note', 'crs')}*`}
                    multiline
                    rows={4}
                    fullWidth
                  />
                </Stack>
              </Grid>

              <Grid item md={12} container direction="row">
                <Button
                  variant="contained"
                  color="primary"
                  fullWidth
                  type="submit"
                  disabled={isSubmitting || disabledButton}
                >
                  Save
                </Button>
              </Grid>
            </Grid>
          </FormProvider>
        </CardContent>
      </DialogContent>
    </Dialog>
  );
}
